import { useI18n } from '@muse/i18n'
import { type TDetailsRow } from '@ds/components'
import {
    convertMillisecondsToSeconds,
    convertSecondsToTime,
    formatSize,
    formatUploadDate,
} from '@core/format'
import { DeferredHydration } from '@muse-web/router'
import { hasClickableLinks } from '@core/linkify'
import { getCategoryById, splitAudioGenres, type TAudioSimple } from '@core/models'
import { useCurrentUser } from '@features/session'
import { isAudioOwner } from '@features/audio'
import { isAnonymousAuthor } from '@features/author'
import { MusehubBannerContainer } from '@features/musehub'
import { buildCollectionTagLinks } from '@features/collection'
import { InsightsPromoBannerContainer } from '@features/insights-promo'
import { getGenreUrl, getSubgenreUrl } from '@core/routes'
import { getSearchUrl } from '@features/search'

import { AudioDetailsGroup } from '../components/audio-details-group'
import { AudioDetailsLayout } from '../components/audio-details-layout/AudioDetailsLayout'
import { GenreLinks } from '../components/genre-links'
import { AudioDetails } from '../components/audio-details/AudioDetails'
import type { TAudioDetailsProps } from '../components/audio-details/AudioDetails.types'
import { AsyncLinkifiedAudioDetails } from '../components/audio-details/AsyncLinkifiedAudioDetails'
import { AsyncAudioLicense } from '../components/audio-license/AsyncAudioLicense'
import { AudioMusicDetails } from '../components/audio-music-details/AudioMusicDetails'
import { AUDIO_TAGS_COPY } from '../constants/audioTagsCopy'

import { AudioStatsContainer } from './AudioStatsContainer'
import { AudioSuggestionsContainer } from './AudioSuggestionsContainer'
import { AudioTranscriptionSectionContainer } from './AudioTranscriptionSectionContainer'
import { MadeWithAudacityBannerContainer } from './MadeWithAudacityBannerContainer'

const BITS_IN_KILOBIT = 1000

type TAudioDetailsContainerProps = {
    audio: TAudioSimple
}

export const AudioDetailsContainer = ({ audio }: TAudioDetailsContainerProps) => {
    const { t } = useI18n()

    const category = getCategoryById(audio.categoryId)
    const isAnonymous = isAnonymousAuthor(audio.authorUsername)
    const { username: myUsername } = useCurrentUser()
    const isOwner = isAudioOwner(audio, myUsername)

    const { links } = buildCollectionTagLinks(audio.tags, audio.crossLinkTags, audio.tags.length)

    const uploadedDate = formatUploadDate(audio.dateCreated)
    const { genres, subgenres } = splitAudioGenres(audio.genres ?? [])
    const duration = convertSecondsToTime(convertMillisecondsToSeconds(audio.duration))
    const bitrate = `${audio.source.bitRate / BITS_IN_KILOBIT} kbps`

    const rawRows: (TDetailsRow | null)[] = [
        audio.title ? { label: t('i18n.audioDetailTitle'), value: audio.title } : null,
        audio.authorDisplayName
            ? { label: t('i18n.audioDetailAuthor'), value: audio.authorDisplayName }
            : null,
        category ? { label: t('i18n.audioDetailCategory'), value: category.title } : null,
        genres.length > 0
            ? {
                  label: t('i18n.audioDetailGenre'),
                  value: (
                      <GenreLinks
                          items={genres.map(genre => ({
                              id: genre.slug,
                              title: genre.title,
                              href: getGenreUrl(genre.slug),
                          }))}
                      />
                  ),
              }
            : null,
        subgenres.length > 0
            ? {
                  label: t('i18n.audioDetailSubgenre'),
                  value: (
                      <GenreLinks
                          items={subgenres.map(subgenre => ({
                              id: subgenre.slug,
                              title: subgenre.title,
                              href: subgenre.parentTitle
                                  ? getSubgenreUrl(subgenre.slug)
                                  : undefined,
                          }))}
                      />
                  ),
              }
            : null,
        audio.duration ? { label: t('i18n.audioDetailDuration'), value: duration } : null,
        audio.source.type
            ? { label: t('i18n.audioDetailFormat'), value: audio.source.type.toUpperCase() }
            : null,
        audio.source.bitRate ? { label: t('i18n.audioDetailBitrate'), value: bitrate } : null,
        audio.source.size
            ? { label: t('i18n.audioDetailSize'), value: formatSize(audio.source.size) }
            : null,
        audio.dateCreated
            ? {
                  label: t('i18n.audioDetailUploaded'),
                  value: uploadedDate,
                  excludeFromPageGuardian: true,
              }
            : null,
    ]
    const rows = rawRows.filter((row): row is TDetailsRow => row !== null)

    const moreInfo = {
        title: audio.title,
        author: audio.authorDisplayName,
        duration,
        bitrate,
        date: uploadedDate,
    }

    const details: TAudioDetailsProps = {
        description: audio.description,
        descriptionEmptyLabel: isOwner
            ? t('i18n.audioDescriptionEmptyOwner')
            : t('i18n.audioDescriptionEmpty'),
        isUnlisted: !audio.isPublic,
        categoryTitle: category?.title,
        categoryHref: category ? getSearchUrl({ category: category.name }) : undefined,
        noCategoryLabel: AUDIO_TAGS_COPY.noCategory,
        tags: links,
    }

    return (
        <AudioDetailsLayout>
            {hasClickableLinks(audio.authorUsername) ? (
                <AsyncLinkifiedAudioDetails {...details} />
            ) : (
                <DeferredHydration>
                    <AudioDetails {...details} />
                </DeferredHydration>
            )}
            <AudioDetailsGroup>
                {audio.license === 'none' ? null : (
                    <AsyncAudioLicense
                        license={audio.license}
                        learnMoreLabel={t('i18n.audioLicenseLearnMore')}
                    />
                )}
                <DeferredHydration>
                    <AudioStatsContainer audio={audio} />
                </DeferredHydration>
                {audio.source.from === 'audacity' ? (
                    <MadeWithAudacityBannerContainer audioId={audio.id} />
                ) : null}
            </AudioDetailsGroup>
            <DeferredHydration>
                <InsightsPromoBannerContainer />
            </DeferredHydration>
            <AudioTranscriptionSectionContainer audio={audio} />
            <DeferredHydration>
                <AudioMusicDetails
                    heading={t('i18n.audioMoreInfoHeading')}
                    moreInfo={moreInfo}
                    rows={rows}
                    detailsAriaLabel={t('i18n.audioDetailsAriaLabel')}
                />
            </DeferredHydration>
            {isAnonymous ? (
                <MusehubBannerContainer banner="creatorMusicSfxBundle" />
            ) : (
                <AudioSuggestionsContainer audio={audio} />
            )}
        </AudioDetailsLayout>
    )
}
